import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import type { LicenseProofDisplayModel, LicenseProofRefundInfo } from '../utils/licenseProofDisplay';
import { formatProofDateTime } from '../utils/licenseProofDisplay';
import { licenseService } from '../services/licenseService';

const PrintGlobals = createGlobalStyle`
  @media print {
    body * {
      visibility: hidden !important;
    }
    .jlicense-proof-surface,
    .jlicense-proof-surface * {
      visibility: visible !important;
    }
    .jlicense-proof-surface {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 16px !important;
      box-shadow: none !important;
      border: none !important;
      background: #fff !important;
      color: #111 !important;
    }
    .jlicense-proof-no-print {
      display: none !important;
    }
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  /* 내부(Surface) 스크롤 없이 오버레이만 세로 스크롤 — 위·아래 여백으로 끝까지 여유 있게 탐색 */
  padding: clamp(32px, 6vh, 56px) 16px clamp(40px, 8vh, 80px);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

const Dialog = styled.div`
  width: min(520px, 100%);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Surface = styled.div`
  position: relative;
  background: #fff;
  color: #111;
  border-radius: 12px;
  padding: 20px 48px 20px 22px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid #e8e8e8;
  overflow: visible;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #666;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #f0f0f0;
    color: #111;
  }
`;

const AgencyLinkButton = styled.button`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: none;
  background: var(--color-accent-strong, #1b4332);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.72;
    cursor: wait;
  }
`;

const ReceiptClickNotice = styled.div`
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #ffd966;
  color: #5c4a00;
`;

const DocTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 1.15rem;
  text-align: center;
  font-weight: 800;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  &:last-of-type {
    border-bottom: none;
  }
`;

const RowLabel = styled.span`
  color: #555;
  flex-shrink: 0;
`;

const RowValue = styled.span`
  text-align: right;
  word-break: break-all;
`;

const EmphasisRowLabel = styled(RowLabel)`
  font-weight: 600;
  font-size: 14px;
  color: #333;
`;

const EmphasisRowValue = styled(RowValue)`
  font-weight: 700;
  font-size: 15px;
  color: #111;
`;

const BuyerSection = styled.div`
  padding: 6px 0 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const BuyerSubRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 4px 0;
  font-size: 13px;
`;

const BuyerSubLabel = styled.span`
  color: #555;
  flex-shrink: 0;
`;

const BuyerSubValue = styled.span`
  text-align: right;
  word-break: break-all;
  font-weight: 700;
  font-size: 15px;
  color: #111;
`;

const BuyerSubValueMuted = styled(BuyerSubValue)`
  font-weight: 400;
  font-size: 13px;
  color: #888;
`;

const AmountBlock = styled.div`
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #ddd;
`;

const AmountRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 4px 0;
`;

const TotalRow = styled(AmountRow)`
  align-items: baseline;
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid #ccc;
`;

const PaymentTotalLabel = styled.span`
  font-weight: 700;
  font-size: 16px;
  color: #333;
`;

const PaymentTotalAmount = styled.span`
  font-weight: 800;
  font-size: 20px;
  color: #111;
`;

const RefundBlock = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #f8f4f4;
  border: 1px solid #e8d4d4;
  border-radius: 8px;
`;

const RefundBlockTitle = styled.div`
  font-weight: 700;
  font-size: 13px;
  color: #8b2e2e;
  margin-bottom: 8px;
`;

const RefundPgNote = styled.div`
  font-size: 11px;
  color: #8b5a5a;
  margin-bottom: 6px;
`;

const RefundLoadingNote = styled.div`
  font-size: 12px;
  color: #666;
  padding: 8px 0;
`;

const RefundDetailLine = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-align: right;
`;

const RefundTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e0c8c8;
`;

const RefundTotalLabel = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: #8b2e2e;
`;

const RefundTotalAmount = styled.span`
  font-weight: 800;
  font-size: 18px;
  color: #8b2e2e;
`;

const GrayBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #f4f4f4;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  color: #333;
`;

const BoxTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
  font-size: 13px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: stretch;
`;

const GhostButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid var(--color-accent, #2d6a4f);
  background: var(--color-surface, #fff);
  color: var(--color-accent-strong, #1b4332);
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: var(--color-panel, #f6f7f6);
    border-color: var(--color-accent-strong, #1b4332);
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  border: none;
  background: var(--color-accent-strong, #1b4332);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    filter: brightness(1.06);
  }
`;

/** Firebase Callable 오류는 code만 오거나 message가 "internal"로만 올 수 있음 */
function formatFirebaseCallableErrorMessage(e: unknown): string {
  const o = e as { code?: string; message?: string; details?: string };
  const msg = typeof o?.message === 'string' ? o.message.trim() : '';
  if (msg && msg.toLowerCase() !== 'internal') return msg;
  const code = typeof o?.code === 'string' ? o.code : '';
  if (code === 'functions/internal' || msg.toLowerCase() === 'internal') {
    return 'PortOne 영수증 URL 조회에 실패했습니다. Firebase Functions에 getPortonePaymentReceiptUrl 배포, PORTONE_SECRET_KEY(포트원 REST API Secret), 실결제 모드(appSettings/billing.paymentMode)를 확인해 주세요.';
  }
  if (typeof o?.details === 'string' && o.details.trim()) return o.details.trim();
  if (e instanceof Error && e.message) return e.message;
  return '요청 처리에 실패했습니다.';
}

type PgCancellationPayload = {
  totalCancelledKrw: number;
  paidTotalKrw: number;
  lastCancelledAtIso: string | null;
  items: Array<{
    amountKrw: number;
    cancelledAtIso: string | null;
    reason?: string;
    supplyAmountKrw?: number;
    vatAmountKrw?: number;
  }>;
};

function buildRefundInfoFromPgCancellation(
  pg: PgCancellationPayload,
  model: LicenseProofDisplayModel
): LicenseProofRefundInfo {
  const detailLines: string[] = ['포트원(PG) 등록 취소 내역 기준'];
  const latest = pg.items.length > 0 ? pg.items[pg.items.length - 1] : null;
  if (latest?.supplyAmountKrw != null && latest?.vatAmountKrw != null) {
    detailLines.push(
      `과세 ${latest.supplyAmountKrw.toLocaleString()}원 + 부가세 ${latest.vatAmountKrw.toLocaleString()}원`
    );
  }
  if (pg.items.length > 1) {
    for (const item of pg.items) {
      const when = item.cancelledAtIso ? formatProofDateTime(item.cancelledAtIso, null) : '';
      const reason = item.reason ? ` · ${item.reason}` : '';
      detailLines.push(`취소 ${item.amountKrw.toLocaleString()}원${when ? ` (${when})` : ''}${reason}`);
    }
  }
  return {
    refundedAtDisplay: pg.lastCancelledAtIso ? formatProofDateTime(pg.lastCancelledAtIso, null) : '-',
    refundAmountKrw: pg.totalCancelledKrw,
    paidAmountKrw: pg.paidTotalKrw > 0 ? pg.paidTotalKrw : model.totalAmount,
    detailLines,
    isPgSourced: true,
  };
}

interface LicenseProofDocumentModalProps {
  open: boolean;
  onClose: () => void;
  model: LicenseProofDisplayModel | null;
  /** PortOne paymentId — 있으면 PortOne API로 공식 영수증 URL·구매자명 등 조회 */
  portonePaymentId?: string | null;
}

export default function LicenseProofDocumentModal({
  open,
  onClose,
  model,
  portonePaymentId,
}: LicenseProofDocumentModalProps) {
  const captureRef = useRef<HTMLDivElement>(null);
  const title = '구매 영수증';

  const [pgReceiptUrl, setPgReceiptUrl] = useState<string | null>(null);
  const [pgLoading, setPgLoading] = useState(false);
  const [pgFetchDone, setPgFetchDone] = useState(false);
  const [pgErrorMsg, setPgErrorMsg] = useState<string | null>(null);
  const [receiptClickNotice, setReceiptClickNotice] = useState<string | null>(null);
  const [paymentMethodDisplay, setPaymentMethodDisplay] = useState('카드');
  const [buyerNameDisplay, setBuyerNameDisplay] = useState('-');
  const [refundFromPg, setRefundFromPg] = useState<LicenseProofRefundInfo | null>(null);
  const [pgRefundLoading, setPgRefundLoading] = useState(false);
  const [pgRefundFetchDone, setPgRefundFetchDone] = useState(false);
  const [pgRefundFetchError, setPgRefundFetchError] = useState<string | null>(null);

  const fetchOfficial = !!portonePaymentId;
  const expectsRefund = model?.expectsRefundInfo === true;
  const refundDisplay =
    refundFromPg ?? (model?.refund?.isPgSourced ? model.refund : null);
  const showRefundLoading =
    fetchOfficial && expectsRefund && (pgRefundLoading || pgLoading) && !refundDisplay;

  useEffect(() => {
    let cancelled = false;
    if (!open || !model) {
      setPgReceiptUrl(null);
      setPgLoading(false);
      setPgFetchDone(false);
      setPgErrorMsg(null);
      setReceiptClickNotice(null);
      setPaymentMethodDisplay('카드');
      setBuyerNameDisplay('-');
      setRefundFromPg(null);
      setPgRefundLoading(false);
      setPgRefundFetchDone(false);
      setPgRefundFetchError(null);
      return;
    }

    setReceiptClickNotice(null);
    setRefundFromPg(null);
    setPgRefundFetchError(null);
    const stored = model.storedPgReceiptUrl ?? null;
    setPgReceiptUrl(stored);
    setPgErrorMsg(null);
    setPaymentMethodDisplay(model.cardType || '카드');
    setBuyerNameDisplay(model.buyerName || '-');

    if (!fetchOfficial) {
      setPgLoading(false);
      setPgFetchDone(true);
      setPgRefundFetchDone(true);
      return;
    }

    const needsReceiptUrl = !stored;
    const paymentId = portonePaymentId as string;

    if (expectsRefund) {
      setPgRefundLoading(true);
      setPgRefundFetchDone(false);
      licenseService
        .getPortonePaymentCancellation(paymentId)
        .then((r) => {
          if (cancelled) return;
          if (r.error) {
            setPgRefundFetchError(r.error);
            return;
          }
          const pg = r.pgCancellation;
          if (pg && pg.totalCancelledKrw > 0) {
            setRefundFromPg(buildRefundInfoFromPgCancellation(pg, model));
            setPgRefundFetchError(null);
          } else {
            setPgRefundFetchError(
              'PortOne 결제 건에서 취소 내역을 찾지 못했습니다. Functions 배포 후 다시 시도하거나 매출전표에서 확인해 주세요.'
            );
          }
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setPgRefundFetchError(formatFirebaseCallableErrorMessage(e));
        })
        .finally(() => {
          if (!cancelled) {
            setPgRefundLoading(false);
            setPgRefundFetchDone(true);
          }
        });
    } else {
      setPgRefundFetchDone(true);
    }

    setPgLoading(true);
    setPgFetchDone(false);
    licenseService
      .getPortonePaymentReceiptUrl(paymentId)
      .then((r) => {
        if (cancelled) return;
        if (r.receiptUrl) {
          setPgReceiptUrl(r.receiptUrl);
          setPgErrorMsg(null);
        } else if (needsReceiptUrl) {
          setPgErrorMsg(
            '공식 매출전표 확인 주소를 찾지 못했습니다. 잠시 후 다시 시도하거나 포트원 관리자 콘솔·카드사 앱에서 확인해 주세요.'
          );
        }
        if (r.paymentMethodDisplay) {
          setPaymentMethodDisplay(r.paymentMethodDisplay);
        }
        if (r.buyerName) {
          setBuyerNameDisplay(r.buyerName);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (needsReceiptUrl) setPgErrorMsg(formatFirebaseCallableErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) {
          setPgLoading(false);
          setPgFetchDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    portonePaymentId,
    model,
    model?.orderId,
    model?.storedPgReceiptUrl,
    model?.cardType,
    model?.buyerName,
    model?.expectsRefundInfo,
    fetchOfficial,
    expectsRefund,
  ]);

  const handleReceiptLinkClick = useCallback(() => {
    setReceiptClickNotice(null);
    if (pgLoading) return;
    if (pgReceiptUrl) {
      window.open(pgReceiptUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const msg =
      pgErrorMsg ||
      (pgFetchDone
        ? '공식 매출전표 확인 주소를 찾지 못했습니다. 잠시 후 다시 시도하거나 포트원 관리자 콘솔·카드사 앱에서 확인해 주세요.'
        : '영수증 확인 주소를 불러오는 중입니다. 잠시 후 다시 눌러 주세요.');
    setReceiptClickNotice(msg);
  }, [pgLoading, pgReceiptUrl, pgErrorMsg, pgFetchDone]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handlePdf = useCallback(async () => {
    if (!captureRef.current || !model) return;
    const el = captureRef.current;
    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      ignoreElements: (node: Element) =>
        (node as HTMLElement).classList?.contains?.('jlicense-proof-skip-pdf') ?? false,
    } as any);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const imgW = pageW - margin * 2;
    const imgH = (canvas.height * imgW) / canvas.width;
    let h = imgH;
    if (h > pageH - margin * 2) {
      h = pageH - margin * 2;
    }
    pdf.addImage(imgData, 'PNG', margin, margin, imgW, h, undefined, 'FAST');
    const safeName = title.replace(/\s+/g, '_');
    pdf.save(`${safeName}_${model.orderId}.pdf`);
  }, [model, title]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !model) return null;

  const m = model.merchant;

  return (
    <>
      <PrintGlobals />
      <Overlay className="jlicense-proof-no-print" onClick={onClose} role="presentation">
        <Dialog onClick={(e) => e.stopPropagation()}>
          <Surface>
            <ModalCloseButton
              type="button"
              className="jlicense-proof-no-print"
              aria-label="닫기"
              onClick={onClose}
            >
              ×
            </ModalCloseButton>
            <div ref={captureRef} className="jlicense-proof-surface">
              <DocTitle>{title}</DocTitle>
              <Row>
                <RowLabel>주문번호</RowLabel>
                <RowValue>{model.orderId}</RowValue>
              </Row>
              <BuyerSection>
                <BuyerSubRow>
                  <BuyerSubLabel>구매자명</BuyerSubLabel>
                  <BuyerSubValue>{buyerNameDisplay}</BuyerSubValue>
                </BuyerSubRow>
                <BuyerSubRow>
                  <BuyerSubLabel>계정 이메일</BuyerSubLabel>
                  <BuyerSubValueMuted>{model.accountEmail}</BuyerSubValueMuted>
                </BuyerSubRow>
              </BuyerSection>
              <Row>
                <EmphasisRowLabel>구매상품</EmphasisRowLabel>
                <EmphasisRowValue>{model.orderName}</EmphasisRowValue>
              </Row>
              <Row>
                <RowLabel>결제일시</RowLabel>
                <RowValue>{model.paidAtDisplay}</RowValue>
              </Row>
              <Row>
                <RowLabel>결제방법</RowLabel>
                <RowValue>{paymentMethodDisplay}</RowValue>
              </Row>
              <AmountBlock>
                <AmountRow>
                  <span>과세 금액</span>
                  <span>{model.supplyAmount.toLocaleString()}</span>
                </AmountRow>
                <AmountRow>
                  <span>비과세 금액</span>
                  <span>{model.taxFreeAmount.toLocaleString()}</span>
                </AmountRow>
                <AmountRow>
                  <span>부가세 금액</span>
                  <span>{model.vatAmount.toLocaleString()}</span>
                </AmountRow>
                <TotalRow>
                  <PaymentTotalLabel>결제 금액 ({model.currency})</PaymentTotalLabel>
                  <PaymentTotalAmount>{model.totalAmount.toLocaleString()}원</PaymentTotalAmount>
                </TotalRow>
              </AmountBlock>
              {showRefundLoading && (
                <RefundBlock>
                  <RefundBlockTitle>환불 정보</RefundBlockTitle>
                  <RefundLoadingNote>PG 취소 내역을 불러오는 중…</RefundLoadingNote>
                </RefundBlock>
              )}
              {expectsRefund && pgRefundFetchDone && !refundDisplay && !showRefundLoading && (
                <RefundBlock>
                  <RefundBlockTitle>환불 정보</RefundBlockTitle>
                  <RefundLoadingNote>
                    {pgRefundFetchError ||
                      'PG 취소 내역을 불러오지 못했습니다. Functions(getPortonePaymentCancellation) 배포 후 다시 시도하거나, 아래 「매출전표 확인 창」에서 KG이니시스 취소 금액을 확인해 주세요.'}
                  </RefundLoadingNote>
                </RefundBlock>
              )}
              {refundDisplay && (
                <RefundBlock>
                  <RefundBlockTitle>환불 정보</RefundBlockTitle>
                  {refundDisplay.isPgSourced && (
                    <RefundPgNote>KG이니시스·포트원에 등록된 실제 카드 취소(환불) 내역입니다.</RefundPgNote>
                  )}
                  <Row>
                    <RowLabel>환불일시</RowLabel>
                    <RowValue>{refundDisplay.refundedAtDisplay}</RowValue>
                  </Row>
                  <Row>
                    <RowLabel>결제 금액</RowLabel>
                    <RowValue>{refundDisplay.paidAmountKrw.toLocaleString()}원</RowValue>
                  </Row>
                  {refundDisplay.detailLines?.map((line) => (
                    <RefundDetailLine key={line}>{line}</RefundDetailLine>
                  ))}
                  <RefundTotalRow>
                    <RefundTotalLabel>환불 금액</RefundTotalLabel>
                    <RefundTotalAmount>{refundDisplay.refundAmountKrw.toLocaleString()}원</RefundTotalAmount>
                  </RefundTotalRow>
                </RefundBlock>
              )}
              <GrayBox>
                    <BoxTitle>판매처 ({m.tradeName})</BoxTitle>
                    <div>대표자: {m.representative}</div>
                    <div>사업자등록번호: {m.bizRegNo}</div>
                    <div>통신판매업신고: {m.mailOrderNo}</div>
                    <div>주소: {m.address}</div>
                    <div>연락처: {m.phone}</div>
                    <div>결제·상담: {m.paymentEmail}</div>
                  </GrayBox>
                  <GrayBox>
                    <div>본 결제는 KG이니시스를 통해 처리되었습니다.</div>
                    <div style={{ marginTop: 6 }}>
                      공식 매출전표는 KG이니시스 영수증 확인 창을 통해 확인할 수 있습니다.
                    </div>
              </GrayBox>
            </div>
          </Surface>
          <ButtonRow className="jlicense-proof-no-print">
            <>
              <AgencyLinkButton
                  type="button"
                  disabled={pgLoading}
                  onClick={handleReceiptLinkClick}
                >
                  {pgLoading ? '영수증 확인 주소 불러오는 중…' : '매출전표 확인 창 바로가기'}
                </AgencyLinkButton>
                {receiptClickNotice && <ReceiptClickNotice>{receiptClickNotice}</ReceiptClickNotice>}
            </>
            <SubButtonRow>
              <GhostButton type="button" onClick={handlePrint}>
                인쇄
              </GhostButton>
              <PrimaryButton type="button" onClick={handlePdf}>
                PDF 저장
              </PrimaryButton>
            </SubButtonRow>
          </ButtonRow>
        </Dialog>
      </Overlay>
    </>
  );
}
